import { AlertTriangle, Lightbulb, TrendingUp, CheckCircle } from "lucide-react"
import type { UseCaseOverview } from "../types"

interface OverviewSectionProps {
  /** Overview content configuration */
  overview: UseCaseOverview
}

/**
 * Overview section showing challenge, solution, and impacts.
 * Displayed as a three-column grid on desktop.
 */
export function OverviewSection({ overview }: OverviewSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-3">
          {/* Challenge */}
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="mb-3 text-lg font-semibold text-neutral-900">
              The Challenge
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600">
              {overview.challenge}
            </p>
          </div>

          {/* Solution */}
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Lightbulb className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-3 text-lg font-semibold text-neutral-900">
              Our Solution
            </h3>
            <p className="text-sm leading-relaxed text-neutral-600">
              {overview.solution}
            </p>
          </div>

          {/* Impact */}
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="mb-3 text-lg font-semibold text-neutral-900">
              The Impact
            </h3>
            <ul className="space-y-2">
              {overview.impacts.map((impact, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-neutral-600"
                >
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  {impact}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
